import { AscendProgramLevels } from '@allurion/domain';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  safePolygon,
} from '@floating-ui/react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PartnerIcon } from './AscendPartnerLevel';

export function AscendProgramHelp() {
  const { t } = useTranslation();

  return (
    <div className="px-8 py-6 shadow-lg bg-white border border-secondary text-primary">
      <ul className="flex gap-0">
        {AscendProgramLevels.filter(({ level }) => level > 0).map(({ title, level }) => (
          <li key={level} className="flex flex-col items-center justify-center gap-2">
            <PartnerIcon level={level} className="w-4/5" />
            <span className="text-center text-xs">
              {t(`${title} Partner`, {
                defaultValue: {
                  'CC': 'Certified Clinic',
                  'AC': 'Advanced Center',
                  'CE': 'Center of Excellence',
                }[title],
              })}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-xs">
        {t(
          'The different levels are determined based on the number of Allurion Balloon Treatments performed and the Weight-loss achievements of the clinics, organized from Center of Excellence (highest) to Certified Clinic.'
        )}
      </p>
    </div>
  );
}

export function AscendProgramHelpTrigger({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(15),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
  });

  // Event listeners to change the open state
  const hover = useHover(context, { move: false, restMs: 100, handleClose: safePolygon() });
  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' });

  // Detect touch device
  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  // Merge interactions based on device
  const interactions = isTouchDevice
    ? [click, focus, dismiss, role]
    : [hover, focus, dismiss, role];
  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

  return (
    <>
      <button type="button" className={className} ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </button>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-20 w-[490px] max-w-full relative p-1"
          >
            <button
              type="button"
              className="absolute top-1 right-3 text-lg md:hidden"
              onClick={() => setIsOpen(false)}
            >
              x
            </button>
            <AscendProgramHelp />
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
